/**
 * Administration → Storage → Transfer windows (1.1.0): named weekly hours when storage migrations and tiering rules
 * may move files. The page (a list with "used by", an editor with period rows and a time zone, a week view of open
 * hours) and the picker the migration wizard and the tiering rule dialog use.
 *
 * Times are the window's own wall-clock times in its time zone; the week view shows this week (Monday to Sunday in
 * that time zone), so a change of summer time shows as a shorter or a longer period.
 */
import { useMemo, useState } from 'react';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import {
  DEFAULT_SCHEDULE_TIMEZONE, WEEKDAYS, describeWindow, openIntervals, wallTimeOf, zonedTimeToUtc,
  type TransferWindow, type TransferWindowBody, type TransferWindowDTO, type TransferWindowPeriod, type Weekday,
} from '@prism/contracts';
import { errorDetail } from '../../../api/client';
import { useMe } from '../../../api/hooks/me';
import { useTransferWindowMutations, useTransferWindows } from '../../../api/hooks/transferWindows';
import { Modal } from '../../../components/Modal';
import { TableScroll } from '../../../components/TableScroll';
import { toast } from '../../../components/Toasts';

const DAY_SHORT: Record<Weekday, string> = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
const DAY_MS = 86_400_000;

const ZONES: string[] = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch {
    return [];
  }
})();

/** "Tue 23:00" in the viewer's own time. */
function shortWhen(iso: string): string {
  return new Intl.DateTimeFormat(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(iso));
}

/** "Open now, closes Tue 05:00" / "Closed, opens Tue 23:00". */
export function windowState(w: Pick<TransferWindowDTO, 'openNow' | 'nextChangeAt'>): string {
  if (w.openNow) return w.nextChangeAt ? `Open now, closes ${shortWhen(w.nextChangeAt)}` : 'Open now';
  return w.nextChangeAt ? `Closed, opens ${shortWhen(w.nextChangeAt)}` : 'Closed for the next 8 days';
}

const usedByText = (w: TransferWindowDTO): string => {
  const parts = [
    ...w.usedBy.rules.map((r) => `rule ${r.name}`),
    ...w.usedBy.migrations.map((m) => `migration ${m.name}`),
  ];
  return parts.length ? parts.join(', ') : 'Nothing';
};

const useIsAdmin = () => Boolean(useMe().data?.permissions.includes('admin'));

/** The Transfer windows sub-view. */
export function TransferWindows() {
  const q = useTransferWindows();
  const { remove } = useTransferWindowMutations();
  const admin = useIsAdmin();
  const [editing, setEditing] = useState<TransferWindowDTO | 'new' | null>(null);
  const items = q.data?.items ?? [];

  async function del(w: TransferWindowDTO) {
    if (!window.confirm(`Delete the transfer window “${w.name}”?`)) return;
    try {
      await remove.mutateAsync(w.id);
      toast(`Deleted ${w.name}`);
    } catch (err) {
      toast(errorDetail(err), 'error');
    }
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <h2 style={{ margin: 0 }}>Transfer windows</h2>
        <span style={{ flex: 1 }} />
        {admin && <button className="btn" onClick={() => setEditing('new')} data-testid="new-window"><Plus size={14} /> New window</button>}
      </div>
      <p className="lede">
        Named weekly hours when storage migrations and tiering rules may move files, for example nights and weekends. Outside its window a
        migration or a rule starts no new transfers; Run now on a rule ignores the window. A window can't be deleted while anything uses it.
      </p>
      <div className="panel-card" style={{ padding: 0 }} data-testid="transfer-windows">
        {q.isLoading ? <p className="muted" style={{ padding: 16, margin: 0 }}>Loading…</p>
          : items.length === 0 ? <p className="muted" style={{ padding: 16, margin: 0 }}>No transfer windows yet: migrations and rules run at any time.</p> : (
            <TableScroll>
            <table className="table compact">
              <thead><tr><th>Window</th><th>Now</th><th>Used by</th><th /></tr></thead>
              <tbody>
                {items.map((w) => {
                  const used = w.usedBy.rules.length + w.usedBy.migrations.length > 0;
                  return (
                    <tr key={w.id} data-testid="transfer-window" data-name={w.name} style={{ cursor: 'default' }}>
                      <td className="wrap" style={{ minWidth: 200 }}>
                        <strong>{w.name}</strong>
                        <div className="faint" style={{ fontSize: 12 }} data-testid="window-description">{w.description}</div>
                      </td>
                      <td>{w.openNow ? <span className="pill accent">Open</span> : <span className="pill">Closed</span>}
                        <div className="faint" style={{ fontSize: 12 }}>{windowState(w)}</div></td>
                      <td className="wrap" data-testid="window-used-by">{usedByText(w)}</td>
                      <td className="actions">
                        {admin && <>
                          <button className="btn ghost sm" onClick={() => setEditing(w)} aria-label={`Edit ${w.name}`} data-testid="window-edit"><Pencil size={13} /></button>
                          <button className="btn ghost sm danger" onClick={() => void del(w)} disabled={used} aria-label={`Delete ${w.name}`} data-testid="window-delete"
                            title={used ? `Used by ${usedByText(w)}: choose another window there first` : undefined}><Trash2 size={13} /></button>
                        </>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </TableScroll>
          )}
      </div>
      {editing && (
        <Modal title={editing === 'new' ? 'New transfer window' : `Edit ${editing.name}`} wide onClose={() => setEditing(null)}>
          <WindowEditor initial={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />
        </Modal>
      )}
    </>
  );
}

interface Draft {
  name: string;
  timezone: string;
  periods: TransferWindowPeriod[];
}

const NIGHTS: TransferWindowPeriod[] = [{ days: ['mon', 'tue', 'wed', 'thu', 'fri'], from: '23:00', to: '05:00' }];

/** What is wrong with a draft, or null. The API checks the same with TransferWindowBody. */
function draftProblem(d: Draft): string | null {
  if (!d.name.trim()) return 'Give the window a name.';
  if (!d.periods.length) return 'Add at least one period.';
  if (d.periods.some((p) => !p.days.length)) return 'Choose at least one day for each period.';
  const hhmm = /^([01]\d|2[0-3]):[0-5]\d$/;
  if (d.periods.some((p) => !hhmm.test(p.from) || !(p.to === '24:00' || hhmm.test(p.to)))) return 'Times are HH:MM.';
  return null;
}

/**
 * Name, time zone and period rows (days, from, to), with the description and the week view of what it gives.
 * Creates a window (initial null) or replaces one; onDone gets the saved window (undefined when cancelled).
 */
function WindowEditor({ initial, onDone, idPrefix = 'window' }: { initial: TransferWindowDTO | null; onDone: (saved?: TransferWindowDTO) => void; idPrefix?: string }) {
  const { create, update } = useTransferWindowMutations();
  const [d, setD] = useState<Draft>(() => ({
    name: initial?.name ?? '',
    timezone: initial?.timezone ?? DEFAULT_SCHEDULE_TIMEZONE,
    // A time input has no 24:00: 00:00 ends at the next midnight just the same.
    periods: (initial?.periods ?? NIGHTS).map((p) => ({ ...p, days: [...p.days], to: p.to === '24:00' ? '00:00' : p.to })),
  }));
  const busy = create.isPending || update.isPending;
  const problem = draftProblem(d);
  const draft: TransferWindow | null = problem ? null : { timezone: d.timezone, periods: d.periods };
  const setPeriod = (i: number, p: Partial<TransferWindowPeriod>) => setD((x) => ({ ...x, periods: x.periods.map((q, j) => (j === i ? { ...q, ...p } : q)) }));
  const toggleDay = (i: number, day: Weekday) => {
    const p = d.periods[i]!;
    const days = p.days.includes(day) ? p.days.filter((x) => x !== day) : WEEKDAYS.filter((x) => x === day || p.days.includes(x));
    setPeriod(i, { days });
  };

  async function save() {
    const body: TransferWindowBody = { name: d.name.trim(), timezone: d.timezone, periods: d.periods };
    try {
      const saved = initial ? await update.mutateAsync({ id: initial.id, body }) : await create.mutateAsync(body);
      toast(initial ? `Saved ${saved.name}` : `Added ${saved.name}`);
      onDone(saved);
    } catch (err) {
      toast(errorDetail(err), 'error');
    }
  }

  return (
    <div data-testid="window-editor">
      <div className="field-row">
        <div className="field"><label htmlFor={`${idPrefix}-name`}>Name</label>
          <input id={`${idPrefix}-name`} className="input" value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} placeholder="Nights and weekends" data-testid="window-name" /></div>
        <div className="field"><label htmlFor={`${idPrefix}-tz`}>Time zone</label>
          {ZONES.length ? (
            <select id={`${idPrefix}-tz`} className="select" value={d.timezone} onChange={(e) => setD({ ...d, timezone: e.target.value })} data-testid="window-timezone">
              {!ZONES.includes(d.timezone) && <option value={d.timezone}>{d.timezone}</option>}
              {ZONES.map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
          ) : <input id={`${idPrefix}-tz`} className="input" value={d.timezone} onChange={(e) => setD({ ...d, timezone: e.target.value })} data-testid="window-timezone" />}
        </div>
      </div>
      <div className="field" style={{ marginBottom: 8 }}>
        <label>Open</label>
        {d.periods.map((p, i) => (
          <div key={i} data-testid="window-period" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
            <div role="group" aria-label={`Days of period ${i + 1}`} style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {WEEKDAYS.map((day) => (
                <button key={day} type="button" className={`chip ${p.days.includes(day) ? 'active' : ''}`} aria-pressed={p.days.includes(day)}
                  style={p.days.includes(day) ? undefined : { background: 'transparent', color: 'var(--muted)', borderColor: 'var(--border)' }}
                  onClick={() => toggleDay(i, day)} data-testid={`window-day-${day}`}>{DAY_SHORT[day]}</button>
              ))}
            </div>
            <label className="faint" htmlFor={`${idPrefix}-from-${i}`}>from</label>
            <input id={`${idPrefix}-from-${i}`} className="input" type="time" style={{ width: 110 }} value={p.from} onChange={(e) => setPeriod(i, { from: e.target.value })} data-testid="window-from" />
            <label className="faint" htmlFor={`${idPrefix}-to-${i}`}>to</label>
            <input id={`${idPrefix}-to-${i}`} className="input" type="time" style={{ width: 110 }} value={p.to} onChange={(e) => setPeriod(i, { to: e.target.value })} data-testid="window-to" />
            <button type="button" className="btn ghost sm" aria-label={`Remove period ${i + 1}`} disabled={d.periods.length === 1}
              onClick={() => setD({ ...d, periods: d.periods.filter((_, j) => j !== i) })}><X size={13} /></button>
          </div>
        ))}
        <div><button type="button" className="btn ghost sm" onClick={() => setD({ ...d, periods: [...d.periods, { days: ['sat', 'sun'], from: '00:00', to: '00:00' }] })} data-testid="window-add-period"><Plus size={13} /> Add period</button></div>
        <span className="help">A period belongs to the day it starts: Mon 23:00–05:00 runs into Tuesday morning. The same time twice is 24 hours.</span>
      </div>
      {problem ? <p className="help" style={{ color: 'var(--danger)' }} data-testid="window-problem">{problem}</p>
        : <p className="help" data-testid="window-preview">{describeWindow(draft)}</p>}
      {draft && <WeekView window={draft} />}
      <div className="actions" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <button type="button" className="btn ghost" onClick={() => onDone()}>Cancel</button>
        <button type="button" className="btn primary" disabled={Boolean(problem) || busy} onClick={() => void save()} data-testid="window-save">{initial ? 'Save window' : 'Add window'}</button>
      </div>
    </div>
  );
}

/** This week's open hours, Monday to Sunday in the window's time zone, one bar per day. */
export function WeekView({ window: w, now = Date.now() }: { window: TransferWindow; now?: number }) {
  const days = useMemo(() => {
    const t = wallTimeOf(now, w.timezone);
    const today = Date.UTC(t.y, t.mo - 1, t.d);
    const mondayIndex = (new Date(today).getUTCDay() + 6) % 7;
    return WEEKDAYS.map((day, i) => {
      const date = new Date(today + (i - mondayIndex) * DAY_MS);
      const next = new Date(date.getTime() + DAY_MS);
      const start = zonedTimeToUtc({ y: date.getUTCFullYear(), mo: date.getUTCMonth() + 1, d: date.getUTCDate(), h: 0, mi: 0, s: 0 }, w.timezone);
      const end = zonedTimeToUtc({ y: next.getUTCFullYear(), mo: next.getUTCMonth() + 1, d: next.getUTCDate(), h: 0, mi: 0, s: 0 }, w.timezone);
      const len = end - start;
      const open = openIntervals(w, start, end).map((iv) => ({ left: ((iv.start - start) / len) * 100, width: ((iv.end - iv.start) / len) * 100 }));
      const hours = openIntervals(w, start, end).reduce((s, iv) => s + (iv.end - iv.start), 0) / 3_600_000;
      return { day, label: `${DAY_SHORT[day]} ${date.getUTCDate()}.${date.getUTCMonth() + 1}.`, open, hours, nowAt: now >= start && now < end ? ((now - start) / len) * 100 : null };
    });
  }, [w, now]);
  const total = days.reduce((s, d) => s + d.hours, 0);
  return (
    <div data-testid="window-week" style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', background: 'var(--surface)' }}>
      <div style={{ display: 'flex', fontSize: 11, marginBottom: 4 }} className="faint">
        <span style={{ width: 72 }}>This week</span>
        <span style={{ flex: 1, display: 'flex', justifyContent: 'space-between' }}>{['00', '06', '12', '18', '24'].map((h) => <span key={h}>{h}</span>)}</span>
        <span style={{ width: 48, textAlign: 'right' }}>hours</span>
      </div>
      {days.map((d) => (
        <div key={d.day} style={{ display: 'flex', alignItems: 'center', height: 20 }} data-testid="window-week-day" data-day={d.day} data-hours={Math.round(d.hours * 10) / 10}>
          <span style={{ width: 72, fontSize: 12 }} className="muted">{d.label}</span>
          <span style={{ flex: 1, position: 'relative', height: 12, background: 'var(--bg)', borderRadius: 3, overflow: 'hidden' }}>
            {d.open.map((o, i) => <span key={i} style={{ position: 'absolute', top: 0, bottom: 0, left: `${o.left}%`, width: `${o.width}%`, background: 'var(--accent)', opacity: 0.75 }} />)}
            {d.nowAt != null && <span title="Now" style={{ position: 'absolute', top: -2, bottom: -2, left: `${d.nowAt}%`, width: 2, background: 'var(--text)' }} />}
          </span>
          <span style={{ width: 48, textAlign: 'right', fontSize: 12 }} className="faint">{Math.round(d.hours * 10) / 10}</span>
        </div>
      ))}
      <div className="faint" style={{ fontSize: 11, marginTop: 4 }}>{Math.round(total * 10) / 10} hours open this week, in {w.timezone}</div>
    </div>
  );
}

const NEW = '__new__';

/**
 * Choose a window, or Any time (null). allowCreate: offer New window… (admins only), which opens the editor in place
 * and chooses the window it saves.
 */
export function TransferWindowPicker({ value, onChange, allowCreate = false, id }: {
  value: string | null;
  onChange: (windowId: string | null) => void;
  allowCreate?: boolean;
  id?: string;
}) {
  const q = useTransferWindows();
  const admin = useIsAdmin();
  const [creating, setCreating] = useState(false);
  const items = q.data?.items ?? [];
  const canCreate = allowCreate && admin;
  const chosen = items.find((w) => w.id === value);
  return (
    <>
      <select id={id} className="select" value={creating ? NEW : (value ?? '')} data-testid="window-picker"
        onChange={(e) => {
          if (e.target.value === NEW) { setCreating(true); return; }
          setCreating(false);
          onChange(e.target.value || null);
        }}>
        <option value="">Any time</option>
        {value && !chosen && !q.isFetching && <option value={value}>(a window that is gone)</option>}
        {items.map((w) => <option key={w.id} value={w.id}>{w.name} · {w.description}</option>)}
        {canCreate && <option value={NEW}>New window…</option>}
      </select>
      {chosen && !creating && <span className="help" data-testid="window-picker-state">{windowState(chosen)}</span>}
      {creating && (
        <div className="panel-card" style={{ marginTop: 8 }} data-testid="window-inline-create">
          <WindowEditor initial={null} idPrefix={`${id ?? 'picker'}-new`} onDone={(saved) => {
            setCreating(false);
            if (saved) onChange(saved.id);
          }} />
        </div>
      )}
    </>
  );
}
